import React from "react";
import { TrendingUp, BarChart3, PlayCircle, Zap } from "lucide-react";
import { Tooltip } from "./Tooltip";
import { MetricCard } from "./MetricCard";

export const InfluencerData = ({ userDatas }) => {
  if (!userDatas) return null;

  return (
    <div className="space-y-8 mb-8 px-4 sm:px-6">
      {/* Post Data Section */}
      <section className="mb-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Post data</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          <MetricCard
            value={userDatas?.profile?.[0]?.avg_likes ?? 0}
            label="Avg likes"
            className="h-full"
            icon={
              <Tooltip description="The average sum of likes on the last 30 posts." />
            }
          />

          <MetricCard
            value={userDatas?.profile?.[0]?.avg_comments ?? 0}
            label="Avg comments"
            className="h-full"
            icon={
              <Tooltip description="The average sum of comments on the last 30 posts." />
            }
          />

          {/* Engagement Rate Card - Enhanced Design */}
          <div className="bg-white rounded-2xl p-6 border border-green-100 shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col justify-between row-span-2">
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="text-3xl font-bold text-green-600">
                    {(userDatas?.profile?.[0]?.engagement_rate * 100)?.toFixed(
                      2
                    ) || "0.00"}
                    %
                  </div>
                </div>
              </div>
              <div className="text-lg font-semibold text-gray-900 mb-2 flex gap-2">
                Engagement Rate{" "}
                <Tooltip description="Average likes and comments divided by followers." />
              </div>
            </div>

            <div className="relative w-full" style={{ height: 80 }}>
              {/* Background Track */}
              <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-3 bg-gray-100 rounded-full" />

              {/* Progress Indicator */}
              <div
                className="absolute left-0 top-1/2 -translate-y-1/2 h-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-1000"
                style={{
                  width: `${Math.min(
                    100,
                    (userDatas?.profile?.[0]?.engagement_rate || 0) * 2
                  )}%`,
                }}
              />

              {/* Tick marks */}
              {[...Array(21)]?.map((_, i) => (
                <div
                  key={i}
                  className="absolute"
                  style={{
                    left: `${(i / 20) * 100}%`,
                    top: "50%",
                    width: 2,
                    height: i % 5 === 0 ? 20 : 10,
                    background: i % 5 === 0 ? "#9ca3af" : "#d1d5db",
                    transform: "translate(-50%, -50%)",
                    borderRadius: 1,
                    zIndex: 1,
                  }}
                />
              ))}

              {/* Median Indicator */}
              <div
                className="absolute flex flex-col items-center"
                style={{
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -70%)",
                  zIndex: 2,
                }}
              >
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                  <TrendingUp className="w-4 h-4 text-white" />
                </div>
                <div className="text-xs font-semibold text-green-600 mt-2 bg-green-50 px-2 py-1 rounded-full">
                  Industry Median
                </div>
              </div>
            </div>
          </div>

          <MetricCard
            value={userDatas?.profile?.[0]?.avg_post_impressions ?? 0}
            label="Avg post impressions"
            className="h-full md:col-span-2 "
          />

          <MetricCard
            value={userDatas?.profile?.[0]?.estimated_post_price ?? 0}
            label="Estimated Post Price"
            className="h-full"
            format="currency"
            icon={<Tooltip description="Estimated Post Price" />}
          />

          <MetricCard
            value={userDatas?.profile?.[0]?.paid_post_performance ?? 0}
            label="Paid Post Performance"
            className="h-full"
            format="percentage"
            icon={
              <Tooltip description="This metric compares the average engagement of disclosed sponsored posts to that of organic content." />
            }
          />

          <MetricCard
            value={userDatas?.profile?.[0]?.cpe ?? 0}
            label="CPE"
            className="h-full"
            format="currency"
            icon={
              <Tooltip description="Average cost per engagement, based on benchmark." />
            }
          />
        </div>
      </section>

      {/* Earned Media Value Section */}
      <section className="mb-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            Earned Media Value
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <MetricCard
            value={userDatas?.emv?.[0]?.emv_by_post ?? 0}
            label="Post"
            format="currency"
            accent="green"
          />
          <MetricCard
            value={userDatas?.emv?.[0]?.emv_by_reels ?? 0}
            label="Reels"
            format="currency"
            accent="emerald"
          />
          <MetricCard
            value={userDatas?.emv?.[0]?.emv_by_story ?? 0}
            label="Story"
            format="currency"
            accent="green"
          />
          <div className="rounded-2xl p-6 bg-white text-green-500 border border-green-100 shadow-lg hover:shadow-xl transition-all duration-300 col-span-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold">Total EMV</h3>
            </div>
            <div className="text-3xl font-bold mb-2">
              {userDatas?.emv?.[0]?.total
                ? new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(userDatas.emv[0].total)
                : "$0"}
            </div>
          </div>
        </div>
      </section>

      {/* Reels Data Section */}
      <section className="mb-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
            <PlayCircle className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Reels data</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6 mb-6">
          <MetricCard
            value={userDatas?.post_stats?.[0]?.avg_reels_plays ?? 0}
            label="Avg reels plays"
            icon={
              <Tooltip description="Average sum of reels plays on the last 30 reels." />
            }
          />
          <MetricCard
            value={userDatas?.post_stats?.[0]?.engagements ?? 0}
            label="Reels engagements"
            icon={
              <Tooltip description="Average total reels engagements of recent reels." />
            }
          />
          <MetricCard
            value={
              userDatas?.post_stats?.[0]?.engagement_rate
                ? (userDatas?.post_stats?.[0]?.engagement_rate * 100).toFixed(1)
                : 0
            }
            label="Reels engagement rate"
            format="percentage"
            icon={
              <Tooltip description="Average engagement rate of average reels views." />
            }
          />
          <MetricCard
            value={userDatas?.post_stats?.[0]?.price ?? 0}
            label="Reels price"
            format="currency"
            icon={<Tooltip description="Average price for 1 IG reels post." />}
          />

          <MetricCard
            value={userDatas?.post_stats?.[0]?.cpp ?? 0}
            label="CPP"
            format="currency"
            icon={
              <Tooltip description="Average cost per video play, based on benchmark." />
            }
          />
          <MetricCard
            value={userDatas?.post_stats?.[0]?.cpe ?? 0}
            label="CPE"
            format="currency"
            icon={
              <Tooltip description="Average cost per engagement, based on benchmark." />
            }
          />
        </div>
      </section>
    </div>
  );
};
